/**
 * Numeric position between two neighbour positions: a randomized point strictly
 * between the neighbours, or a fixed step beyond a lone neighbour.
 *
 * **`upper` and `lower` are POSITIONAL, not numeric bounds.** `upper` is the
 * neighbour rendered ABOVE the insertion point and `lower` the one BELOW. Every
 * list that reorders through this helper — the kanban board, workflow stages,
 * issue-type fields — is sorted ASCENDING, so `upper` normally holds the
 * SMALLER number. That is why a missing `lower` (dropping past the last row)
 * steps UP from `upper`, and a missing `upper` (dropping above the first row)
 * steps DOWN from `lower`. Read them as "the row above / the row below", never
 * as "the high / low end of the interval".
 *
 * Jitter (rather than the exact midpoint) is deliberate: repeatedly dropping into
 * the same gap with a fixed `(upper+lower)/2` halves it each time until the value
 * collides with a neighbour at float precision, producing duplicate sort orders.
 * A random point in the middle of the gap makes such a collision negligibly
 * unlikely while staying strictly between the neighbours. Callers must therefore
 * treat the result as an opaque position, not a reproducible midpoint.
 *
 * The library's single source of truth for drag-reorder positioning.
 */
export function calculateSortOrder(
  upper?: number | null,
  lower?: number | null,
): number {
  if (upper != null && lower != null) {
    // Random point in the middle 50% of the gap — always strictly between the
    // neighbours (so a raw `+ Math.random()` can't overshoot a sub-1 gap).
    return lower + (upper - lower) * (0.25 + Math.random() * 0.5);
  }
  if (upper != null) return upper + 1000;
  if (lower != null) return lower - 1000;
  return 1000;
}
